// PaidSocialPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from './paidxs.png'

const PaidSocialPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        // Scroll animation observer
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const paidSocialStats = [
        { number: '4.9B', label: 'active social media users worldwide' },
        { number: '73%', label: 'of marketers say social media is effective for business' },
        { number: '2.5x', label: 'higher engagement on paid social vs organic posts' },
        { number: '55%', label: 'of consumers discover brands through social media ads' }
    ];

    const paidSocialApproaches = [
        {
            icon: 'fas fa-chess',
            title: 'Paid Social Strategy & Planning',
            desc: 'Comprehensive audience segmentation, platform selection, and campaign roadmap development aligned with business objectives including awareness, traffic, leads, and sales goals.'
        },
        {
            icon: 'fas fa-layer-group',
            title: 'Cross-Platform Campaign Management',
            desc: 'Multi-platform campaign setup and optimization across Meta (Facebook & Instagram), TikTok, LinkedIn, Pinterest, Snapchat, and other relevant social networks with customized targeting.'
        },
        {
            icon: 'fas fa-paint-brush',
            title: 'Creative Ad Development & Optimization',
            desc: 'Scroll-stopping creative development including video ads, motion formats, carousel experiences, and interactive content tailored to each platform\'s unique audience and engagement patterns.'
        },
        {
            icon: 'fas fa-chart-line',
            title: 'Campaign Optimization & Performance Analytics',
            desc: 'Continuous performance monitoring, A/B testing on audiences and creatives, KPI optimization, and comprehensive reporting dashboards with actionable insights.'
        },
        {
            icon: 'fas fa-retweet',
            title: 'Retargeting & Conversion Funnel Strategy',
            desc: 'Advanced retargeting audience development, website pixel tracking setup, dynamic retargeting campaigns, and multi-step conversion funnel optimization.'
        },
        {
            icon: 'fas fa-globe',
            title: 'Platform Specialization & Best Practices',
            desc: 'Deep expertise in platform-specific advertising including Meta\'s targeting capabilities, TikTok\'s engaging formats, LinkedIn\'s B2B solutions, and Pinterest\'s discovery advertising.'
        }
    ];

    const benefits = [
        ['Precision Social Audience Targeting', 'Reach highly specific demographics, interests, and behavioral segments across multiple social platforms'],
        ['Enhanced Brand Visibility & Engagement', 'Increase brand awareness and customer interaction through native social advertising formats'],
        ['Measurable ROI & Performance Tracking', 'Track campaign performance with granular analytics and optimize for maximum return on advertising spend'],
        ['Cross-Platform Campaign Integration', 'Coordinate messaging and targeting across multiple social networks for cohesive brand presence'],
        ['Creative Optimization & Testing Advantage', 'Continuously refine ad creative based on real-time engagement data and audience response']
    ];

    const targetBusinesses = [
        'E-commerce brands seeking to drive product sales and catalog engagement through social commerce',
        'B2B companies generating qualified leads and pipeline development through professional network advertising',
        'Local businesses increasing foot traffic, calls, and local conversions through geo-targeted social campaigns',
        'Consumer brands building visibility, preference, and customer loyalty through engaging social content',
        'Service providers strengthening digital presence and lead generation through targeted social advertising',
        'Growth-focused organizations scaling customer acquisition through data-driven social media strategies'
    ];

    const paidSocialQuestions = [
        'How can you reach your ideal customers across multiple social platforms simultaneously?',
        'What creative formats drive the highest engagement and conversions on each social network?',
        'How do you optimize ad spend across Facebook, Instagram, TikTok, and LinkedIn for maximum efficiency?',
        'What audience segmentation strategies work best for different social media platforms?',
        'How can you leverage social retargeting to nurture leads and reduce acquisition costs?'
    ];

    return (
        <div className="paidmedia sv-page">
            <div className="sv-decorative-image-container">

            </div>

            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            {/* Page Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Paid Social Advertising</span> Services
                    </h1>
                    <p>Strategic campaigns across social platforms that drive engagement, conversions, and measurable growth</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Content Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    Paid social media advertising puts your brand front and centre where audiences spend their time online — in feeds, stories, videos and social streams across platforms like Meta, TikTok, LinkedIn, Pinterest, Snapchat and more.
                                </p>
                                <p className="sv-description">
                                    At Reachtix, our <strong>Paid Social services</strong> combine audience insight, creative strategy and performance optimisation to deliver measurable growth and maximise your advertising impact across the social landscape.
                                </p>
                                <p className="sv-description">
                                    Unlike free posts that rely on algorithms for visibility, paid social campaigns allow you to directly reach precisely targeted audiences based on behaviors, interests, demographics and intent. With billions of users actively engaging on social networks daily, paid social is essential for brands seeking to increase awareness, drive engagement, generate leads, and grow revenue.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Launch Paid Social Campaign
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-chart-line mr-2"></i>
                                        Get Social Media Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            The <span className="sv-text-gradient">Paid Social Advantage</span>
                        </h2>
                        <Row className="sv-stats-grid">
                            {paidSocialStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* What Is Paid Social Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What Is Paid Social Advertising?</h2>
                                <p className="sv-section-description">
                                    Paid social refers to sponsored advertising on social media platforms that appears alongside or within users' organic content. It enables brands to reach specific audiences with precision, bypassing organic algorithm limitations to deliver targeted messaging directly to potential customers.
                                </p>
                                <p className="sv-section-description">
                                    This makes paid social essential not just for awareness, but for building comprehensive marketing funnels that deliver measurable results across the customer journey — from initial discovery to conversion and retention.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Paid Social Questions We Address:</h4>
                                    {paidSocialQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Paid Social Section */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Paid Social Drives Digital Success</h2>
                                <p className="sv-section-description">
                                    Paid social transforms social media presence into measurable business outcomes, enabling brands to engage audiences where they're most active and receptive to messaging.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-bullseye"></i>
                                        <h5>Advanced Audience Targeting</h5>
                                        <p>Reach specific demographics, interests, and behaviors with platform-native targeting</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-video"></i>
                                        <h5>Engaging Content Formats</h5>
                                        <p>Leverage Stories, Reels, video ads, and interactive formats for higher engagement</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-line"></i>
                                        <h5>Performance Optimization</h5>
                                        <p>Continuous campaign refinement based on real-time engagement and conversion data</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-shopping-cart"></i>
                                        <h5>Social Commerce Integration</h5>
                                        <p>Direct path from social discovery to purchase through shoppable ads and features</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Paid Social Approach Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our Paid Social Capabilities at Reachtix</h2>
                                <p className="sv-section-description">
                                    We offer comprehensive paid social solutions across all major platforms, tailored to your specific business objectives, target audience demographics, and competitive social landscape.
                                </p>

                                <div className="sv-approaches-grid">
                                    {paidSocialApproaches.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="mt-5 sv-section-title">Benefits of Paid Social with Reachtix</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Strategic Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Who Should Invest Section */}
                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Benefits Most from Paid Social Advertising?</h2>
                                <p className="sv-section-description">
                                    Paid social delivers maximum value for organizations seeking to leverage social platforms for measurable business growth and customer engagement:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    Regardless of industry, paid social helps you meet customers where they are — engaged, receptive, and ready to take action on social platforms they use daily.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Final CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Amplify Your Social Advertising Impact?</h2>
                                <p className="sv-cta-description">
                                    Unlock the full potential of your social advertising with Reachtix's Paid Social services. Transform your social presence from cost center to revenue generator with campaigns that connect, convert, and scale.
                                </p>
                                <p className="sv-cta-description">
                                    At Reachtix, we treat <strong>paid social</strong> as a strategic growth channel — not just an advertising expense. Our specialists blend platform expertise with business insight to make your social advertising a powerful driver of measurable business outcomes.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='primary'
                                    >
                                        <i className="fas fa-thumbs-up mr-2"></i>
                                        Start Social Campaign
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='light'
                                    >
                                        <i className="fas fa-search mr-2"></i>
                                        Get Free Social Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default PaidSocialPage;